// legacy panel
import type { Disposable } from "@/shared/contracts/lifecycle";
import type { HoverIdleSnapshot } from "../types";
import { UpgradeInteractionController } from "./upgradeInteractionController";
import {
  createUpgradeTooltip,
  type UpgradeTooltipOptions,
} from "./upgradeTooltipView";
import { HoverPointerModel } from "../input/hoverPointerModel";
import type {
  UpgradeRepeatInitialIntervalSource,
  UpgradeRepeatSpeedMultiplierSource,
} from "./upgradeRepeatTiming";

export interface UpgradePanelState {
  readonly effectText: string;
  readonly costText: string;
  readonly cardLabelText: string;
  readonly isAffordable: boolean;
  readonly tooltipEffectText: string;
  readonly tooltipAfterText: string;
  readonly tooltipCostText: string;
  readonly tooltipStateText: string;
}

export interface UpgradePanelViewOptions {
  readonly cardAriaLabel: string;
  readonly tooltip: UpgradeTooltipOptions;
  readonly pointerModel: HoverPointerModel;
  readonly initialIntervalMs?: UpgradeRepeatInitialIntervalSource;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
  readonly onUpgrade: () => boolean;
  readonly render: (snapshot: HoverIdleSnapshot) => UpgradePanelState;
}

export class UpgradePanelView implements Disposable {
  readonly root: HTMLDivElement;
  private readonly render: (snapshot: HoverIdleSnapshot) => UpgradePanelState;
  private readonly button: HTMLButtonElement;
  private readonly costLine: HTMLSpanElement;
  private readonly card: HTMLDivElement;
  private readonly cardLabel: HTMLSpanElement;
  private readonly tooltip: HTMLDivElement;
  private readonly tooltipEffectValue: HTMLSpanElement;
  private readonly tooltipAfterValue: HTMLSpanElement;
  private readonly tooltipCostValue: HTMLSpanElement;
  private readonly tooltipStateValue: HTMLSpanElement;
  private readonly interaction: UpgradeInteractionController;

  constructor(options: UpgradePanelViewOptions) {
    this.render = options.render;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-upgrade-area";

    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.className = "hover-idle-upgrade";
    this.button.dataset.virtualPointer = "allow";
    this.costLine = document.createElement("span");
    this.button.append(this.costLine);

    this.card = document.createElement("div");
    this.card.className = "hover-idle-upgrade-card";
    this.card.tabIndex = 0;
    this.card.setAttribute("aria-label", options.cardAriaLabel);
    this.card.setAttribute("aria-describedby", options.tooltip.id);
    this.cardLabel = document.createElement("span");
    this.card.append(this.cardLabel);

    const tooltip = createUpgradeTooltip(options.tooltip);
    this.tooltip = tooltip.root;
    this.tooltipEffectValue = tooltip.effectValue;
    this.tooltipAfterValue = tooltip.afterValue;
    this.tooltipCostValue = tooltip.costValue;
    this.tooltipStateValue = tooltip.stateValue;

    this.interaction = new UpgradeInteractionController({
      button: this.button,
      card: this.card,
      tooltip: this.tooltip,
      pointerModel: options.pointerModel,
      initialIntervalMs: options.initialIntervalMs,
      speedMultiplier: options.speedMultiplier,
      onUpgrade: ({ nextIntervalMs }) => {
        const upgraded = options.onUpgrade();

        if (upgraded) {
          this.triggerPulse(nextIntervalMs / 1000);
        }
        return upgraded;
      },
    });

    this.root.append(this.button, this.card, this.tooltip);
  }

  update(snapshot: HoverIdleSnapshot): void {
    const state = this.render(snapshot);
    const affordable = state.isAffordable ? "true" : "false";

    this.costLine.textContent = state.costText;
    this.cardLabel.textContent = state.cardLabelText;
    this.card.dataset.affordable = affordable;
    this.tooltip.dataset.affordable = affordable;
    this.button.disabled = !state.isAffordable;
    this.tooltipEffectValue.textContent = state.tooltipEffectText;
    this.tooltipAfterValue.textContent = state.tooltipAfterText;
    this.tooltipCostValue.textContent = state.tooltipCostText;
    this.tooltipStateValue.textContent = state.tooltipStateText;
  }

  private triggerPulse(pulseSeconds: number): void {
    this.button.style.setProperty(
      "--upgrade-pulse-duration",
      `${pulseSeconds.toFixed(3)}s`,
    );
    delete this.button.dataset.pulse;
    void this.button.offsetWidth;
    this.button.dataset.pulse = "true";
  }

  dispose(): void {
    this.interaction.dispose();
  }
}
